import { useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { Box, Grid2, Typography } from "@mui/material";
import { Button, Tooltip } from "@material-ui/core";
import { DataGrid } from "@mui/x-data-grid";
import useStyles from "./AssetCustomTable.styles";
import ReferenceFolderIcon from "../../Utilities/Svg/ReferenceFolderIcon";
import { ArrowSquareIn } from "phosphor-react";
import { Edit3, Settings } from "react-feather";
import { fpatch } from "../../../API/callsAPI";
import { renderFieldContent } from "../../Utilities/CustomTableForAllTrackables/CustomTableBodyForAllTrackables";

const AssetCustomTable = ({
  assetList,
  assetType,
  projectId,
  statusList,
  updateRefreshCounter,
  setIsViewModalOpen,
  fetchFile,
  setSnackValue,
  allFieldsConfig,
  handleEditAssetModalState,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const ref = useRef(null);
  const [hoverOnTaskData, setHoverOnTaskData] = useState({
    isOpen: false,
    task: {},
  });

  const rows = useMemo(
    () =>
      (assetList?.results ?? []).map((res) => ({
        id: res.id,
        ...res,
      })),
    [assetList?.results]
  );

  const handleValueChange = async (fieldName, value, rowId) => {
    try {
      const res = await fpatch({
        url: `trackables/asset/v2/${rowId}/`,
        data: {
          [fieldName]: value,
        },
      });
      if (res.status === 200 || res.status === 201) {
        updateRefreshCounter();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRowClick = (params, event) => {
    const rowId = params?.row?.id;
    if (!rowId) return;

    if (event.ctrlKey) {
      window.open(`/${projectId}/assets/${rowId}/assetversions`);
      sessionStorage.setItem("scrollpos", window.scrollY);
    } else {
      history.push(`/${projectId}/assets/${rowId}/assetversions`);
      sessionStorage.setItem("scrollpos", window.scrollY);
    }
  };

  const columns = [
    ...(allFieldsConfig ?? []).map((field) => ({
      field: field.name,
      headerName: field.label || field.name,
      minWidth: 190,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div
          style={{
            width: "100%",
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          {renderFieldContent({
            field,
            props: { data: params.row },
            ref,
            hoverOnTaskData,
            setHoverOnTaskData,
            handleValueChange: (fieldName, value) =>
              handleValueChange(fieldName, value, params.row.id),
            index: 0,
            statusUrl: `trackables/asset/${params.row.id}/`,
            statusList,
            updateRefreshCounter,
          })}
        </div>
      ),
    })),
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 190,
      sortable: false,
      renderCell: (params) => {
        const data = params.row;

        return (
          <div
            className={classes.flexGap5}
            onClick={(event) => event.stopPropagation()}
          >
            {data && data.media_folders && data.media_folders.media_root_id && (
              <Tooltip title="Asset Files" placement="top">
                <Button
                  size="small"
                  disabled={data.media_folders.media_root_id === null}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (data.media_folders.media_root_id) {
                      window.open(
                        `/${projectId}/project-media/${data.media_folders.media_root_id}`
                      );
                    }
                  }}
                  className={classes.inlineButtons}
                >
                  <ReferenceFolderIcon size={14} />
                </Button>
              </Tooltip>
            )}
            <Button
              size="small"
              className={classes.inlineButtons}
              onClick={(event) => {
                event.stopPropagation();
                fetchFile(data.file ? data.id : null).then(() =>
                  setIsViewModalOpen(true)
                );
              }}
            >
              <span style={{ fontSize: "12px !important" }}>View</span>
            </Button>
            <Tooltip title="view Task" placement="top">
              <Button
                size="small"
                className={classes.inlineButtons}
                onClick={(event) => {
                  event.stopPropagation();
                  history.push(`/${projectId}/assets/${data.id}/task`);
                }}
              >
                <ArrowSquareIn size={18} />
              </Button>
            </Tooltip>
            <Tooltip title="Edit Asset" placement="top">
              <Button
                size="small"
                id="editAssetVersion"
                className={classes.inlineButtons}
                onClick={(e) => handleEditAssetModalState(e, data)}
              >
                <Edit3 size={16} />
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <Box
      className={classes.assetsTableContainer}
      sx={{
        height:
          assetList?.count > 50 ? "calc(100vh - 230px)" : "calc(100vh - 200px)",
        width: "100%",
        overflowX: "auto",
      }}
    >
      {allFieldsConfig.length <= 0 ? (
        <Grid2
          container
          direction="column"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: 200, height: "90%" }}
        >
          <Grid2
            container
            direction="column"
            spacing={2}
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="h6" align="center">
              Custom View Unavailable
            </Typography>
            <Typography align="center">
              Custom view allows you to customize the asset table columns and
              order.
            </Typography>
          </Grid2>
          <Grid2>
            <Button
              variant="outlined"
              onClick={() =>
                window.open(
                  `/${projectId}/settings/field-templates?selectedTrackable=12`,
                  "_blank"
                )
              }
              startIcon={<Settings size={16} />}
            >
              Configure View
            </Button>
          </Grid2>
        </Grid2>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          disableColumnMenu
          disableColumnReorder
          disableSelectionOnClick
          onRowClick={handleRowClick}
          hideFooter
          sx={{
            width: "max-content",
            minWidth: "100%",
            border: "none",
            bgcolor: "transparent",
            "& .MuiDataGrid-root": {
              background: "#1B1D28 !important",
              color: "#606479 !important",
            },
            "& .MuiDataGrid-cell": {
              borderTop: "1px solid #606479 !important",
              alignItems: "center",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              backgroundColor: "#1B1D28",
              color: "#f9f9f9",
              "&:hover": {
                backgroundColor: "#252A38",
              },
            },
            "& .MuiDataGrid-main": {
              minWidth: "max-content",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflowX: "auto",
            },
            "& .MuiDataGrid-window": {
              overflowX: "auto",
            },
            "& .MuiDataGrid-footerContainer": {
              display: "none",
            },
            //
            "& .MuiDataGrid-container--top [role='row'], & .MuiDataGrid-container--bottom [role='row']":
              {
                backgroundColor: "#1B1D28 !important",
                borderColor: "#606479",
              },

            "--DataGrid-rowBorderColor": "#606479",
            "& .MuiDataGrid-columnHeaders": {
              color: "#606479",
              fontSize: {
                xs: "8px",
                sm: "10px",
                md: "12px",
                lg: "14px",
                xl: "16px",
              },
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                No assets found.
              </Box>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default AssetCustomTable;
